import { useEffect, useMemo, useRef } from 'react';
import type { Message, ModelCatalogEntry } from '../types';
import MessageBubble from './MessageBubble';

interface MessageListProps {
  messages: Message[];
  error: string | null;
  models?: ModelCatalogEntry[];
}

export default function MessageList({
  messages,
  error,
  models = [],
}: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Build a modelId -> displayName map so each assistant message can resolve
  // its metrics.modelId to a human-readable label.
  const displayNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of models) map.set(m.modelId, m.displayName);
    return map;
  }, [models]);

  const sorted = [...messages].sort((a, b) => a.timestamp - b.timestamp);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, error]);

  return (
    <div className="message-list" role="log" aria-live="polite">
      {sorted.map((msg) => {
        const displayName = msg.metrics
          ? displayNameById.get(msg.metrics.modelId)
          : undefined;
        return (
          <MessageBubble
            key={msg.id}
            message={msg}
            modelDisplayName={displayName}
          />
        );
      })}
      {error && (
        <div className="message-error" role="alert">
          {error}
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}
